import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useRouter } from 'next/router';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import {
  Grid,
  Button,
  Card,
  MenuItem,
  Stack,
  Skeleton,
  CardHeader,
  CardContent,
  Typography,
  Link,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
// components
import {
  FormProvider,
  RHFTextField,
  RHFSelect,
} from '../../components/hook-form';
import Iconify from '../../components/Iconify';
// hooks
import useResponsive from '../../hooks/useResponsive';
import useAuth from '../../hooks/useAuth';
// swr
import useSWR from 'swr';
// utils
import axiosSWRFetcher from '../../utils/swrFetcher';
import axios from '../../utils/axios';
// constants
import { POSITION_OPTIONS } from '../../constants';
// routes
import { PATH_AUTH } from '../../routes/paths';

const DIOTypo = styled('span')(({ theme }) => {
  return {
    fontFamily: theme.typography.subFontFamily,
    fontWeight: theme.typography.fontWeightBold,
    color: 'inherit',
  };
});

export default function AccountInfo() {
  const router = useRouter();
  const { user, logout } = useAuth();

  const { data, isValidating, mutate } = useSWR(user ? `/api/crews/settings/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const smUp = useResponsive('up', 'sm');

  const UpdatePersonalContractSchema = Yup.object().shape({
    username: Yup.string().required('이름을 입력하세요'),
    nickname: Yup.string().required('닉네임을 입력하세요'),
  });

  const defaultValues = {
    username: data?.crew?.user_name || '',
    nickname: data?.crew?.nickname || '',
    phone: data?.crew?.user_phone || '',
    email: data?.crew?.user_email || '',
    position: data?.crew?.position || '',
  };

  const methods = useForm({
    resolver: yupResolver(UpdatePersonalContractSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
  } = methods;

  useEffect(() => {
    reset({
      username: data?.crew?.user_name || '',
      nickname: data?.crew?.nickname || '',
      phone: data?.crew?.user_phone || '',
      email: data?.crew?.user_email || '',
      position: data?.crew?.position || '',
    });
  }, [data]);

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const postData = {
        ...data,
        username: formData.username,
        nickname: formData.nickname,
      }
      await axios.post(
        `/api/crews/settings/`,
        postData
      );
      enqueueSnackbar('계정 정보가 변경되었습니다.');
      mutate(`/api/crews/settings/`);
      setIsSubmitting(false);
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      router.replace(PATH_AUTH.login);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('로그아웃을 할 수 없습니다', { variant: 'error' });
    }
  };

  return (
    <Grid item xs={12}>
      {(!data || isValidating) && (
      <Stack spacing={3}>
        <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
          <CardContent>
            <Stack spacing={3}>
              <Skeleton variant="text" height={56} />
              <Skeleton variant="text" height={56} />
              <Skeleton variant="text" height={56} />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
      )}
      {data && !isValidating && (
        <FormProvider methods={methods}>
          <Stack spacing={3}>
            <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
              <CardHeader title="개인 정보" />
              <CardContent>
                <Stack spacing={3}>
                  <Stack direction={smUp ? 'row' : 'column'} spacing={3}>
                    <RHFTextField
                      name="username"
                      label="이름"
                    />
                    <RHFTextField
                      name="nickname"
                      label="닉네임"
                    />
                  </Stack>
                  <Stack direction={smUp ? 'row' : 'column'} spacing={3}>
                    <RHFTextField
                      name="email"
                      label="이메일"
                      inputProps={{ readOnly: true }}
                      disabled
                    />
                    <RHFTextField
                      name="phone"
                      label="전화번호"
                      inputProps={{ readOnly: true }}
                      disabled
                    />
                  </Stack>
                  <RHFSelect 
                    fullWidth
                    name="position"
                    label="포지션(직군)"
                    SelectProps={{ native: false }}
                    inputProps={{ readOnly: true }}
                    disabled
                  >
                    {POSITION_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.key}
                        value={option.key}
                        sx={{
                          mx: 1,
                          my: 0.5,
                          borderRadius: 0.75,
                          typography: 'body2',
                        }}
                      >
                        {option.value}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Stack>
                <Stack alignItems="flex-end" sx={{ mt: 1 }}>
                  <Typography
                    variant="caption"
                    color={(theme) => theme.palette.grey[500]}
                  >
                    이메일과 전화번호, 포지션 수정을 원하시면{' '}
                    <Link
                      color={(theme) => theme.palette.grey[500]}
                      href="http://pf.kakao.com/_xbEWUs/chat"
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                    >
                      카카오톡 <DIOTypo>DIO</DIOTypo> 채널
                    </Link>
                    로 문의주세요
                  </Typography>
                </Stack>
                <Stack alignItems="flex-end" sx={{ mt: 3 }}>
                  <LoadingButton
                    onClick={handleSubmit(onSubmit)}
                    type="button"
                    variant="contained"
                    disabled={isValidating}
                    loading={isSubmitting}
                    size="large"
                  >
                    저장하기
                  </LoadingButton>
                </Stack>
              </CardContent>
            </Card>
            <Button
              fullWidth
              variant="contained"
              color="inherit"
              onClick={handleLogout}
              size="large"
              sx={{
                bgcolor: "grey.200",
                disableElevation: true,
                boxShadow: 0,
                justifyContent: 'flex-start',
                '& .MuiButton-endIcon': {
                  marginLeft: 'auto'
                }
              }}
              startIcon={<Iconify icon="eva:log-out-fill" sx={{ color: "text.secondary" }} />}
              endIcon={<Iconify icon="eva:chevron-right-fill" sx={{ color: "text.secondary" }} />}
            >
              로그아웃
            </Button>
          </Stack>
        </FormProvider>
      )}
    </Grid>
  )
}
